/**
 * Session types — mirror of the backend SessionResponse schema.
 */

/** The placeholder title assigned when a session is created without an
 *  explicit name.  The rename dialog starts with an empty input when the
 *  current title is this value, so the user can start typing a real name
 *  immediately instead of first deleting the default. */
export const DEFAULT_SESSION_TITLE = "New Conversation";

/** Persistent marker for normal sessions created from a project task card. */
export const PROJECT_TASK_SESSION_METADATA_KEY = "project_task_session";
/** Marker written by RunManager for a transcript created by a scheduled trigger. */
export const SCHEDULER_SESSION_SOURCE = "scheduler";
/** Marker written by RunManager for a session spawned by a workflow agent step. */
export const WORKFLOW_SESSION_SOURCE = "workflow";
/** Marker written for conversations started through the MCP server. */
export const MCP_SESSION_SOURCE = "mcp";
/** Legacy display marker retained for independently deployed older servers. */
export const WORKFLOW_SESSION_TITLE_PREFIX = "[WF]: ";
/** Default title assigned to a newly created thread session. */
export const DEFAULT_THREAD_TITLE = "New Thread";

/** Additive list-only snapshot of the newest trustworthy session run. */
export interface SessionLatestRun {
  run_id: string;
  status: string;
  is_silent: boolean;
}

/** Additive list-only status for a task related to this session. */
export interface SessionRelatedTaskStatus {
  id: string;
  project_id: string;
  status: string;
  updated_at?: string | null;
  /** Absent on older servers; empty when no live tag belongs to the task. */
  tags?: { id: string; name: string; color: string | null }[];
}

export interface Session {
  id: string;
  tenant_id: string;
  user_id: string;
  platform: string;
  external_workspace_id: string;
  external_channel_id: string;
  external_thread_id: string | null;
  title: string;
  created_at: string;
  last_active_at: string;
  is_archived: boolean;
  metadata: Record<string, any>;
  agent_id: string | null;
  /** Set when this is a sub-agent session spawned by another session.
   *  Top-level (user-created) sessions always have null. Such sub-agent
   *  sessions are hidden from the list endpoint, so this is normally
   *  null on every Session the UI sees. */
  parent_session_id?: string | null;
  /** Project bound at session creation, if this is a project-scoped session. */
  project_id?: string | null;
  /** Child-session type discriminator: "thread", "sub_agent", or null
   *  for top-level sessions. */
  session_type?: string | null;
  /** Whether this session has visible child sessions. */
  has_visible_children?: boolean;
  /** Per-type child session counts. */
  child_counts?: Record<string, number>;
  /** Effective capabilities for the authenticated caller. */
  can_read?: boolean;
  can_participate?: boolean;
  can_manage?: boolean;
  /** True when access is inherited from a live project task binding. */
  is_shared_task_conversation?: boolean;
  /** True when the bound task's project is accessible to other users. */
  is_shared_project?: boolean;
  /** Caller-relative participation discovery; absent on older servers. */
  is_shared_with_me?: boolean;
  is_pinned?: boolean;
  pin_source?: "creator" | "link_open" | "participant" | "manual" | null;
  /** Null is an enriched server's trustworthy idle/no-run state; absence means old server. */
  latest_run?: SessionLatestRun | null;
  /** Present on enriched servers; absence preserves the legacy task lookup. */
  related_tasks?: SessionRelatedTaskStatus[];
  /** Client-only start time of the list request that supplied this row. */
  list_fetch_started_at?: number;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

function isYesterday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return d.getFullYear() === yesterday.getFullYear()
    && d.getMonth() === yesterday.getMonth()
    && d.getDate() === yesterday.getDate();
}

function localDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Group sessions in the same local-date order displayed by the sidebar.
 */
export function groupByDate(
  sessions: readonly Session[],
  todayLabel: string,
  yesterdayLabel: string,
  locale: string,
): { label: string; items: Session[] }[] {
  const groups = new Map<string, { label: string; items: Session[] }>();
  for (const s of sessions) {
    const date = new Date(s.last_active_at);
    const key = localDayKey(date);
    const label = isToday(s.last_active_at)
      ? todayLabel
      : isYesterday(s.last_active_at)
        ? yesterdayLabel
        : date.toLocaleDateString(locale, { month: "short", day: "numeric" });
    const group = groups.get(key) ?? { label, items: [] };
    group.items.push(s);
    groups.set(key, group);
  }
  // Labels are localized presentation text and can repeat across years (for
  // example, "Dec 31"). The numeric local-day key is the grouping identity
  // and sorts correctly across Dec/Jan regardless of locale formatting.
  return [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, group]) => group);
}

export function isProjectTaskSession(session: Pick<Session, "metadata">): boolean {
  return session.metadata?.[PROJECT_TASK_SESSION_METADATA_KEY] === true;
}

/**
 * Scheduled-trigger transcripts are available from schedule-run history, not
 * the user's conversation sidebar. This metadata key predates list-side
 * filtering, so it also safely hides rows when this UI runs against an older
 * server that still returns them from GET /api/sessions.
 */
export function isSchedulerSession(session: Pick<Session, "metadata">): boolean {
  return session.metadata?.source === SCHEDULER_SESSION_SOURCE;
}

/** Missing metadata on an older server means this is an ordinary conversation. */
export function isMcpSession(session: { metadata?: unknown }): boolean {
  return session.metadata != null
    && typeof session.metadata === "object"
    && !Array.isArray(session.metadata)
    && (session.metadata as Record<string, unknown>).source === MCP_SESSION_SOURCE;
}

/**
 * Identify workflow-created sessions across independently deployed server
 * versions. New servers provide a source marker; older reservations can have
 * only execution_origin, while legacy titles retain the prefix.
 */
export function isWorkflowSession(session: {
  title: string;
  metadata?: unknown;
}): boolean {
  const metadata = session.metadata && typeof session.metadata === "object"
    ? session.metadata as Record<string, unknown>
    : undefined;
  const source = metadata?.source;
  const executionOrigin = metadata?.execution_origin;
  return source === WORKFLOW_SESSION_SOURCE
    || session.title.startsWith(WORKFLOW_SESSION_TITLE_PREFIX)
    || (source == null
      && typeof executionOrigin === "string"
      && executionOrigin.startsWith("workflow"));
}

/**
 * Remove the legacy workflow marker only for presentation. Keep an otherwise
 * empty title intact so an unusual legacy row never becomes a blank list item.
 */
export function workflowSessionDisplayTitle(title: string): string {
  if (!title.startsWith(WORKFLOW_SESSION_TITLE_PREFIX)) return title;
  return title.slice(WORKFLOW_SESSION_TITLE_PREFIX.length) || title;
}

export interface SessionCreateRequest {
  title?: string | null;
  platform?: string;
  external_workspace_id?: string | null;
  external_channel_id?: string | null;
  external_thread_id?: string | null;
  metadata?: Record<string, any>;
  agent_id?: string | null;
  /** Bind a top-level session directly to a project without a task. */
  project_id?: string | null;
  /** Only used for sub-agent internal plumbing; UI users should leave this undefined. */
  parent_session_id?: string | null;
}

export interface SessionUpdateRequest {
  title?: string | null;
  is_archived?: boolean | null;
  metadata?: Record<string, any> | null;
  agent_id?: string | null;
}

export interface SessionListResponse {
  sessions: Session[];
  /** True when there are older sessions not included in this page. */
  has_more?: boolean;
  /** Composite cursor "ISO-8601|uuid" for the next page, or null when no more. */
  next_cursor?: string | null;
}

export interface SessionMessageResponse {
  id: string;
  session_id: string;
  run_id: string;
  role: string;
  content: string;
  status: string;
  created_at: string;
  metadata: Record<string, any>;
  scheduled_run?: {
    display_name: string | null;
    cron: string | null;
    timezone: string | null;
  } | null;
  navigation_turn_id?: string | null;
}

export interface SessionMessageListResponse {
  messages: SessionMessageResponse[];
  /** Total number of messages in the session (only set in limit-only mode). */
  total?: number;
  /** True when there are older messages not included in this page. */
  has_more?: boolean;
}

export interface SessionRunResponse {
  session_id: string;
  run_id: string;
  agent_id: string;
  user_message: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  /** True when the framework intentionally ended the turn without a reply. */
  is_silent: boolean;
}

// ─── Session search types (Ctrl+K command palette) ─────────────────────────

export interface SessionSearchResult {
  id: string;
  title: string;
  last_active_at: string;
  agent_id: string | null;
  /** "title" = session title matched; "content" = a message matched */
  match_type: "title" | "content";
  /** Contextual excerpt of the matching message (content matches only) */
  snippet: string | null;
  /** Number of messages that matched in this session */
  match_count?: number;
  message_id?: string | null;
  role?: "user" | "assistant" | null;
  message_at?: string | null;
  rank_band?: number;
  match_start?: number | null;
  match_end?: number | null;
}

export interface SessionSearchResponse {
  results: SessionSearchResult[];
  total: number;
  /** Present only when the server guarantees user/assistant-only content. */
  content_scope?: "user_assistant";
  error?: "search_timeout";
  retry_after_ms?: number;
}
export interface SessionSearchV2Response {
  results: SessionSearchResult[];
  content_scope: "user_assistant";
  has_more: boolean;
  next_cursor: number | null;
  error?: "search_timeout";
  retry_after_ms?: number;
}
export interface QuickSessionSearchResponse {
  sessions: QuickSessionSearchResult[];
  has_more: boolean;
  next_cursor: string | null;
  error?: "search_timeout";
  retry_after_ms?: number;
}
export type QuickSessionSearchResult = Pick<Session, "id" | "title" | "last_active_at" | "agent_id">
  & Partial<Pick<Session, "is_archived" | "session_type" | "parent_session_id" | "can_read">>;
// ─── Thread / child-session types ───────────────────────────────────────────

export type SessionType = "thread" | "sub_agent" | null;

/** The provenance fields needed to render a thread-origin banner. */
export interface ThreadAnchorDisplay {
  source_anchor_kind: "message" | "block";
  source_quote: string;
}

/**
 * Extract the display-safe provenance fields from either a full thread anchor
 * or a lightweight anchor-query reference. Invalid legacy metadata is
 * ignored rather than rendered as a misleading origin banner.
 */
export function threadAnchorDisplayFrom(value: unknown): ThreadAnchorDisplay | null {
  if (!value || typeof value !== "object") return null;
  const anchor = value as Record<string, unknown>;
  if (typeof anchor.source_quote !== "string") return null;

  return {
    source_anchor_kind: anchor.source_anchor_kind === "block" ? "block" : "message",
    source_quote: anchor.source_quote,
  };
}

export interface ThreadAnchor extends ThreadAnchorDisplay {
  schema_version: 1;
  source_message_id: string;
  source_block_index: number | null;
  source_block_type: "paragraph" | null;
  source_quote_sha256?: string;
  source_quote_start?: number | null;
  source_quote_end?: number | null;
  source_quote_prefix?: string | null;
  source_quote_suffix?: string | null;
  source_message_revision?: string | null;
  source_message_sha256?: string | null;
  parent_context_cutoff_message_id?: string | null;
  parent_context_cutoff_revision?: string | null;
}

export interface ThreadCreateAnchor {
  kind: "message" | "block";
  block_index?: number | null;
  block_type?: "paragraph" | null;
}

export interface ThreadCreateRequest {
  source_message_id: string;
  anchor: ThreadCreateAnchor;
  /** Exact rendered message text or selected text, persisted in the thread anchor. */
  source_quote?: string | null;
  source_quote_start?: number | null;
  source_quote_end?: number | null;
  source_quote_prefix?: string | null;
  source_quote_suffix?: string | null;
  expected_source_revision?: string | null;
  expected_source_sha256?: string | null;
  title?: string | null;
}

export interface ThreadCreateResponse {
  created: boolean;
  session: Session;
}

export interface ChildSessionSummary {
  id: string;
  title: string;
  parent_session_id: string;
  session_type: string;
  agent_id?: string | null;
  created_at: string;
  last_active_at: string;
  has_visible_children?: boolean;
  child_counts?: Record<string, number>;
  source_message_id?: string | null;
  source_turn_id?: string | null;
  source_created_at?: string | null;
  anchor_kind?: "message" | "block" | null;
  anchor_state?: "indexed" | "unindexed" | "missing" | "invalid" | null;
}

export interface ChildSessionListResponse {
  items: ChildSessionSummary[];
  next_cursor: string | null;
}

export interface ThreadAnchorRef {
  child_session_id: string;
  agent_id?: string | null;
  source_message_id: string;
  source_anchor_kind: "message" | "block";
  source_block_index?: number | null;
  /** Exact client-rendered origin quote, returned for reloadable panels. */
  source_quote: string;
  source_quote_sha256?: string | null;
  source_quote_start?: number | null;
  source_quote_end?: number | null;
  source_quote_prefix?: string | null;
  source_quote_suffix?: string | null;
  last_active_at: string;
  title?: string | null;
}

export interface ThreadAnchorQueryResponse {
  items: ThreadAnchorRef[];
}
